import React from 'react';
import { connect } from 'react-redux';
import pt from 'prop-types';

import actions from 'partners/actions';
import LoadingView from 'components/view/loading';
import { arrayCapabilitiesToObject } from 'partners/shared/capabilities';
import {
  STORAGE_KEY,
  completeSignup,
  readStoredSteps,
} from 'partners/shared/signup';

import {
  SignupLayout,
  SignupDetails,
  SignupExpertise,
  SignupAccount,
  SignupVerifyCode,
  STEPS,
} from 'partners/components/pages/signup';

const VALIDATE_NAME_DELAY = 500;

const mapStateToProps = (state) => ({
  flash: state.flash,
});

const mapDispatchToProps = (dispatch) => ({
  validatePartner(data) {
    return dispatch(actions.partner.validate(data));
  },

  createLead(data) {
    dispatch(actions.flash.clear());
    return dispatch(actions.partner.createLead(data));
  },

  completeLead(id, data) {
    return dispatch(actions.partner.completeLead(id, data));
  },

  sendLeadCode(id) {
    return dispatch(actions.partner.sendLeadCode(id));
  },

  createPartner(leadId) {
    return dispatch(actions.partner.create(leadId));
  },

  flashError(message) {
    dispatch(actions.flash.error(message));
  },

  flashSuccess(message) {
    dispatch(actions.flash.success(message));
  },
});

export class Signup extends React.Component {
  static propTypes = {
    location: pt.object,
    router: pt.object,
    validatePartner: pt.func,
    createLead: pt.func,
    completeLead: pt.func,
    sendLeadCode: pt.func,
    createPartner: pt.func,
    flashError: pt.func,
    flashSuccess: pt.func,
  };

  static contextTypes = {
    setTitle: pt.func.isRequired,
  };

  static onEnter() {
    window.scrollTo(0, 0);
  }

  constructor(props, context) {
    super(props, context);

    this.state = {
      loaded: false,
      step: 'details',
      details: {},
      expertise: {},
      errors: {},
      leadId: undefined,
      leadCompleted: false,
      accountEmail: undefined,
      takenPartnerId: undefined,
      oauthToken: undefined,
      oauthEmail: undefined,
      onChangePartnerName: this.onChangePartnerName.bind(this),
      onSubmitDetails: this.onSubmitDetails.bind(this),
      onSubmitExpertise: this.onSubmitExpertise.bind(this),
      onSubmitAccount: this.onSubmitAccount.bind(this),
      onSubmitCode: this.onSubmitCode.bind(this),
      onResendEmail: this.onResendEmail.bind(this),
      onStartOAuth: this.onStartOAuth.bind(this),
      onGoToStep: this.onGoToStep.bind(this),
      onBack: this.onBack.bind(this),
    };

    this.validatePartnerNameTimer = 0;
  }

  componentDidMount() {
    const { oauth_token: oauthToken, email } = this.props.location.query;
    const stored = readStoredSteps();

    if (!oauthToken && !stored) {
      this.setState({ loaded: true });
      return;
    }

    // With a token and the steps back there is nothing left to fill in. Without
    // the steps there is no company yet, so the wizard restarts.
    const isFinishing = Boolean(oauthToken && stored);

    this.setState(
      {
        // Held back so the account step does not flash behind the redirect
        loaded: !isFinishing,
        step: stored ? 'account' : 'details',
        details: stored ? stored.details : {},
        expertise: stored ? stored.expertise : {},
        oauthToken,
        oauthEmail: email,
      },
      async () => {
        if (!isFinishing) {
          return;
        }

        const finished = await this.onSubmitAccount({ tab: 'oauth' });

        // The redirect leaves the page up a moment, so only reveal the step
        // when there was none
        if (!finished) {
          this.setState({ loaded: true });
        }
      },
    );
  }

  componentWillUnmount() {
    if (this.validatePartnerNameTimer) {
      clearTimeout(this.validatePartnerNameTimer);
    }
  }

  onChangePartnerName(event, name) {
    const { flashError, validatePartner } = this.props;

    if (this.validatePartnerNameTimer) {
      clearTimeout(this.validatePartnerNameTimer);
      this.validatePartnerNameTimer = 0;
    }

    this.setState((state) => ({
      errors: { ...state.errors, name: undefined },
      takenPartnerId: undefined,
    }));

    if (!name) {
      return;
    }

    this.validatePartnerNameTimer = setTimeout(async () => {
      const result = await validatePartner({ name });

      if (!result) {
        flashError('Something went wrong!');
      } else if (result.error) {
        this.setState((state) => ({
          errors: { ...state.errors, name: result.error },
          takenPartnerId: result.partner_id,
        }));
      }

      this.validatePartnerNameTimer = 0;
    }, VALIDATE_NAME_DELAY);
  }

  onSubmitDetails(values) {
    if (this.validatePartnerNameTimer !== 0) {
      return;
    }

    this.setState({ details: values, step: 'expertise' });
  }

  onSubmitExpertise(values) {
    this.setState({ expertise: values, step: 'account' });
  }

  onGoToStep(step) {
    // Once a lead exists the earlier steps no longer reach it, since
    // `onSubmitAccount` reuses the lead rather than recreating it
    if (this.state.leadId && step !== 'account') {
      return;
    }

    this.setState({ step });
  }

  onBack() {
    this.setState((state) => ({
      step: state.step === 'account' ? 'expertise' : 'details',
    }));
  }

  onStartOAuth() {
    const { details, expertise } = this.state;

    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ details, expertise }),
    );
  }

  async onSubmitAccount(values) {
    const { flashError, createLead, sendLeadCode } = this.props;
    const { details, expertise, oauthToken, oauthEmail, leadId } = this.state;
    const { agree, ...companyInput } = details;
    const { tab, ...accountInput } = values;

    // Confirming the lead carries a code and nothing else, so the account has
    // to be on the lead before we leave this step
    const accountFields = tab === 'new' ? accountInput : undefined;

    // Reuse the lead an earlier attempt created: a second one would collide
    // on the company name the first already took
    let currentLeadId = leadId;

    if (!currentLeadId) {
      const lead = await createLead({
        ...companyInput,
        ...accountFields,
        email: accountInput.email || oauthEmail,
        capabilities: arrayCapabilitiesToObject(expertise.capabilities || []),
      });

      if (!lead) {
        return false;
      }

      if (lead.error || lead.errors) {
        flashError(lead.error || lead.errors || 'An unknown error occurred');
        return false;
      }

      currentLeadId = lead.id;
      this.setState({ leadId: currentLeadId });
    }

    if (tab === 'new') {
      // The only branch that asks for a code, so the only one that sends it
      const sent = await sendLeadCode(currentLeadId);

      if (!sent || sent.error || sent.errors) {
        flashError('Could not send the code, please try again');
        return false;
      }

      this.setState({ step: 'verify', accountEmail: accountInput.email });
      return false;
    }

    const proof =
      tab === 'oauth'
        ? { identity_token: oauthToken }
        : { email: accountInput.email, password: accountInput.password };

    return this.completeAndCreate(currentLeadId, proof);
  }

  async onSubmitCode(values) {
    return this.completeAndCreate(this.state.leadId, { code: values.code });
  }

  async onResendEmail() {
    const { sendLeadCode, flashError, flashSuccess } = this.props;
    const { leadId } = this.state;

    const result = await sendLeadCode(leadId);

    if (!result || result.error || result.errors) {
      flashError('Could not resend the code, please try again');
      return;
    }

    flashSuccess(`Sent email verification to ${this.getAccountEmail()}`);
  }

  getAccountEmail() {
    return this.state.accountEmail || this.state.oauthEmail;
  }

  completeAndCreate(leadId, proof) {
    return completeSignup({
      ...this.props,
      leadId,
      proof,
      leadCompleted: this.state.leadCompleted,
      onLeadCompleted: () => this.setState({ leadCompleted: true }),
    });
  }

  renderStep() {
    switch (this.state.step) {
      case 'verify':
        return (
          <SignupVerifyCode
            onSubmitCode={this.state.onSubmitCode}
            onResendEmail={this.state.onResendEmail}
          />
        );
      case 'expertise':
        return (
          <SignupExpertise
            values={this.state.expertise}
            onSubmit={this.state.onSubmitExpertise}
            onBack={this.state.onBack}
          />
        );
      case 'account':
        return (
          <SignupAccount
            companyName={this.state.details.name}
            onSubmit={this.state.onSubmitAccount}
            onBack={this.state.onBack}
            onStartOAuth={this.state.onStartOAuth}
            canGoBack={!this.state.leadId}
          />
        );
      default:
        return (
          <SignupDetails
            values={this.state.details}
            errors={this.state.errors}
            takenPartnerId={this.state.takenPartnerId}
            onChangePartnerName={this.state.onChangePartnerName}
            onSubmit={this.state.onSubmitDetails}
          />
        );
    }
  }

  render() {
    return (
      <SignupLayout
        {...this.props}
        step={
          STEPS.some((item) => item.id === this.state.step)
            ? this.state.step
            : undefined
        }
        onGoToStep={this.state.onGoToStep}
        canGoBack={!this.state.leadId}
      >
        {this.state.loaded ? this.renderStep() : <LoadingView />}
      </SignupLayout>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Signup);
